/**
 * AdminSkeletons - Admin dashboard loading states
 * UserManagementSkeleton, AnalyticsSkeleton
 */
import React from 'react';
import { Skeleton } from './BaseSkeleton';

export function UserManagementSkeleton() {
  return (
    <div className="p-4 sm:p-6 w-full max-w-[1800px] mx-auto pb-20">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4 gap-2">
          <Skeleton className="h-6 w-6 rounded" />
          <Skeleton className="h-7 w-48" />
        </div>
        <Skeleton className="h-4 w-96 mb-4" />
        
        {/* Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <Skeleton className="h-9 w-28 rounded-md" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-4 rounded" />
            <Skeleton className="h-9 w-32 rounded-md" />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-4 mb-8">
        {Array.from({ length: 18 }).map((_, i) => (
          <div key={i} className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-center">
              <div className="p-2 rounded-lg mr-3 bg-gray-200 dark:bg-gray-800">
                <Skeleton className="h-5 w-5 rounded" />
              </div>
              <div className="min-w-0 flex-1">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-6 w-16 mb-1" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* User Sections */}
      <div className="space-y-8">
        {/* Premium Users Section */}
        <UserSectionSkeleton title="Premium Users" rowCount={5} />

        {/* Trial Users Section */}
        <UserSectionSkeleton title="Trial Users" rowCount={3} />

        {/* Former Trial Users Section */}
        <UserSectionSkeleton title="Former Trial Users" rowCount={3} colCount={6} />

        {/* Basic Users Section */}
        <UserSectionSkeleton title="Basic Users" rowCount={4} colCount={6} />

        {/* Deleted Users Section */}
        <UserSectionSkeleton title="Deleted Users" rowCount={2} colCount={6} isDeleted />
      </div>
    </div>
  );
}

// Helper component for user section skeleton
function UserSectionSkeleton({ title, rowCount = 3, colCount = 7, isDeleted = false }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Skeleton className="h-6 w-6 rounded" />
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-5 w-8 rounded-full" />
        </div>
        <Skeleton className="h-4 w-4 rounded" />
      </div>
      
      {/* User Table */}
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Table Header */}
        <div className="bg-gray-50 dark:bg-gray-800/50 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className={`grid grid-cols-${colCount} gap-4`}>
            {Array.from({ length: colCount }).map((_, i) => (
              <Skeleton key={i} className="h-4 w-20" />
            ))}
          </div>
        </div>
        
        {/* Table Rows */}
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {Array.from({ length: rowCount }).map((_, i) => (
            <div key={i} className="px-6 py-4">
              <div className={`grid grid-cols-${colCount} gap-4 items-center`}>
                <div>
                  <Skeleton className="h-4 w-24 mb-1" />
                  <Skeleton className="h-3 w-32" />
                </div>
                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-6 w-16 rounded-full" />
                <Skeleton className="h-6 w-8 rounded-lg" />
                <div className="flex flex-col items-center space-y-1">
                  <Skeleton className="h-5 w-16 rounded-lg" />
                  <Skeleton className="h-5 w-12 rounded-lg" />
                  {isDeleted && <Skeleton className="h-4 w-12" />}
                </div>
                <Skeleton className={isDeleted ? "h-4 w-24" : "h-6 w-12 rounded-lg"} />
                <Skeleton className="h-8 w-8 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function AnalyticsSkeleton() {
  return (
    <div className="p-4 sm:p-6 w-full max-w-[1800px] mx-auto pb-20">
      {/* Header and controls */}
      <div className="mb-8">
        <div className="flex items-center mb-4 gap-2">
          <Skeleton className="h-6 w-6 rounded" />
          <Skeleton className="h-7 w-72" />
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div className="flex items-center gap-2 w-48">
            <Skeleton className="h-4 w-4 rounded" />
            <Skeleton className="h-9 w-40 rounded-md" />
          </div>
          <Skeleton className="h-9 w-28 rounded-md" />
        </div>
      </div>

      {/* Revenue & Financial Metrics */}
      <MetricsSectionSkeleton title="Revenue & Financial Metrics" count={4} />

      {/* Customer & Growth Metrics */}
      <MetricsSectionSkeleton title="Customer & Growth Metrics" count={4} />

      {/* Trial & Conversion Metrics */}
      <div className="mb-8">
        <Skeleton className="h-6 w-56 mb-6" />
        
        {/* Current Trial Status */}
        <MetricsSubsectionSkeleton title="Current Trial Status" count={3} />

        {/* Conversion Performance */}
        <MetricsSubsectionSkeleton title="Conversion Performance" count={3} />

        {/* Churn & Retention Analysis */}
        <MetricsSubsectionSkeleton title="Churn & Retention Analysis" count={3} />

        {/* Historical Metrics */}
        <MetricsSubsectionSkeleton title="Historical Metrics" count={3} />
      </div>

      {/* User Engagement & Product Metrics */}
      <div className="mb-8">
        <Skeleton className="h-6 w-64 mb-6" />
        
        {/* Active Users */}
        <MetricsSubsectionSkeleton title="Active Users" count={4} />

        {/* Engagement Insights */}
        <div className="mb-6">
          <Skeleton className="h-5 w-36 mb-1" />
          <Skeleton className="h-3 w-64 mb-3" />
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <MetricCardSkeletonItem key={i} />
            ))}
          </div>
        </div>
      </div>

      {/* Platform Activity */}
      <div className="mb-8">
        <Skeleton className="h-6 w-36 mb-4" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center">
                <Skeleton className="h-5 w-5 rounded mr-2" />
                <div>
                  <Skeleton className="h-4 w-32 mb-2" />
                  <Skeleton className="h-6 w-20" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Charts grid */}
      <div className="space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCardSkeleton />
          <ChartCardSkeleton />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCardSkeleton height={280} />
          <ChartCardSkeleton />
        </div>

        {/* Daily signups section */}
        <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-6">
            <Skeleton className="h-6 w-52" />
            <Skeleton className="h-4 w-4 rounded" />
          </div>
          <div className="h-[320px]">
            <Skeleton className="h-full w-full rounded" />
          </div>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-gray-50 dark:bg-transparent backdrop-blur-md rounded-lg p-4">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-6 w-20" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper components for AnalyticsSkeleton
function MetricsSectionSkeleton({ title, count }) {
  return (
    <div className="mb-8">
      <Skeleton className="h-6 w-56 mb-4" />
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {Array.from({ length: count }).map((_, i) => (
          <MetricCardSkeletonItem key={i} />
        ))}
      </div>
    </div>
  );
}

function MetricsSubsectionSkeleton({ title, count }) {
  return (
    <div className="mb-6">
      <Skeleton className="h-5 w-40 mb-3" />
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {Array.from({ length: count }).map((_, i) => (
          <MetricCardSkeletonItem key={i} />
        ))}
      </div>
    </div>
  );
}

function MetricCardSkeletonItem() {
  return (
    <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <div className="flex items-center">
        <div className="p-2 rounded-lg mr-3 bg-gray-200 dark:bg-gray-800">
          <Skeleton className="h-5 w-5 rounded" />
        </div>
        <div className="min-w-0 flex-1">
          <Skeleton className="h-4 w-32 mb-2" />
          <Skeleton className="h-7 w-20 mb-1" />
          <Skeleton className="h-3 w-24" />
        </div>
      </div>
    </div>
  );
}

function ChartCardSkeleton({ height = 300 }) {
  return (
    <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-6">
      <div className="flex items-center gap-2 mb-6">
        <Skeleton className="h-6 w-52" />
        <Skeleton className="h-4 w-4 rounded" />
      </div>
      <div style={{ height: `${height}px` }}>
        <Skeleton className="h-full w-full rounded" />
      </div>
    </div>
  );
}

